import { useEffect, useRef, useCallback, type RefObject } from 'react';

interface UseInfiniteScrollOptions {
  onLoadMore: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  threshold?: string;
}

interface UseInfiniteScrollResult {
  sentinelRef: RefObject<HTMLDivElement | null>;
}

/**
 * Custom hook for infinite scroll functionality using IntersectionObserver.
 * Triggers onLoadMore callback when sentinel element approaches viewport.
 *
 * @param options.onLoadMore - Callback to execute when more content should be loaded
 * @param options.disabled - When true, observer won't trigger callback (default: false)
 * @param options.isLoading - When true, prevents duplicate requests (default: false)
 * @param options.threshold - rootMargin for IntersectionObserver (default: "100px")
 */
export function useInfiniteScroll({
  onLoadMore,
  disabled = false,
  isLoading = false,
  threshold = '100px',
}: UseInfiniteScrollOptions): UseInfiniteScrollResult {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !disabled && !isLoading) {
        onLoadMore();
      }
    },
    [onLoadMore, disabled, isLoading]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Don't create observer if disabled or loading
    if (disabled || !sentinel) {
      return;
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(handleIntersect, {
      rootMargin: threshold,
      threshold: 0,
    });

    observerRef.current.observe(sentinel);

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersect, disabled, threshold]);

  return { sentinelRef };
}

export default useInfiniteScroll;
